import { useEffect, useRef, useState } from "react";
import { Heart, X, Sparkles } from "lucide-react";

/**
 * TippingModal — süßes Trinkgeld-Popup mit eingebettetem Ablefy-Checkout.
 * Props:
 *   open        : boolean       — Modal sichtbar?
 *   onClose     : () => void    — Close-Handler
 *   variant     : "auto" | "manual" — "auto" zeigt zusätzlich "Vielleicht später"-Button
 */
export default function TippingModal({ open, onClose, variant = "manual" }) {
  const [showCheckout, setShowCheckout] = useState(false);
  const wrapperRef = useRef(null);

  // ESC schließt das Modal
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Ablefy-Embed-Script lazy laden, sobald Checkout angezeigt werden soll.
  // WICHTIG: Das offizielle Ablefy-Embed-Script bindet sich an `DOMContentLoaded`.
  // Da wir das Script DYNAMISCH (nach Modal-Open) injizieren, ist DOMContentLoaded
  // längst gefeuert und das Embed würde nie initialisiert. Trick: Wir setzen auf
  // dem Wrapper `data-init-event="ablefy-init"` und dispatchen das Event manuell,
  // sobald das Script geladen ist.
  useEffect(() => {
    if (!showCheckout) return;

    const INIT_EVENT = "ablefy-init-tip";

    // Vor jeder Init: alten Script-Tag + Wrapper-Inhalt + alte Wrapper-DIVs aufräumen
    const cleanup = () => {
      document.querySelectorAll("#embedable-form-script, #elopage-embedable-form-script").forEach((s) => s.remove());
      document.querySelectorAll("script[src*='/assets/embedable_form/']").forEach((s) => s.remove());
      // Falls Ablefy außerhalb unseres Wrappers gerendert hat, alle ablefy-Iframes killen
      document.querySelectorAll("iframe[src*='ablefy'], iframe[src*='myablefy'], iframe[src*='elopage']").forEach((f) => f.remove());
      const w = document.getElementById("embedable-form-wrapper");
      if (w) w.innerHTML = "";
    };
    cleanup();

    // Wrapper mit init-event-Marker versehen
    const wrapper = document.getElementById("embedable-form-wrapper");
    if (wrapper) {
      wrapper.setAttribute("data-init-event", INIT_EVENT);
      wrapper.dataset.token = "H2iwju9Pzus-zQr6nZdK";
    }

    // Loader-Script laden
    const script = document.createElement("script");
    script.id = "embedable-form-script";
    script.src = "https://myablefy.com/assets/embedable_form/index.js";
    script.dataset.token = "H2iwju9Pzus-zQr6nZdK";
    script.defer = true;
    script.onload = () => {
      // Dem Loader Zeit geben, seinen Listener zu registrieren — dann manuell triggern
      setTimeout(() => {
        document.dispatchEvent(new Event(INIT_EVENT));
      }, 50);
    };
    document.body.appendChild(script);

    // Fallback: Falls innerhalb 12s KEIN Ablefy-Iframe erscheint, Direkt-Link als Fallback anzeigen
    const fallbackTimer = setTimeout(() => {
      const w = document.getElementById("embedable-form-wrapper");
      if (!w) return;
      const hasIframe = !!w.querySelector("iframe");
      if (!hasIframe && w.children.length === 0) {
        w.innerHTML = '<div style="padding:24px;text-align:center;color:#1a1a2e;font-family:Outfit,sans-serif">' +
          '<p style="margin:0 0 12px;font-weight:600">Der eingebettete Checkout lädt gerade nicht.</p>' +
          '<a href="https://myablefy.com/s/erfolgbeginntindir/trinkgeld-10ab464b/payment" target="_blank" rel="noopener" ' +
          'style="display:inline-block;padding:10px 20px;background:linear-gradient(135deg,#FF69B4,#FF1493);color:white;text-decoration:none;border-radius:14px;font-weight:600">' +
          'Trinkgeld in neuem Tab geben →</a></div>';
      }
    }, 12000);

    return () => {
      clearTimeout(fallbackTimer);
      cleanup();
    };
  }, [showCheckout]);

  // Wenn das Modal komplett geschlossen wird, Checkout zurücksetzen
  useEffect(() => {
    if (!open) setShowCheckout(false);
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-fade-in"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(8px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="tipping-modal"
    >
      <div
        className={`relative w-full ${showCheckout ? "max-w-3xl" : "max-w-md"} rounded-3xl overflow-hidden shadow-2xl animate-fade-in-up flex flex-col`}
        style={{
          background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f1729 100%)",
          border: "1px solid rgba(255, 105, 180, 0.25)",
          boxShadow: "0 0 60px rgba(255, 105, 180, 0.15), 0 25px 80px rgba(0,0,0,0.6)",
          maxHeight: "92vh",
        }}
      >
        {/* Sanftes Glow oben */}
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[300px] h-[200px] pointer-events-none opacity-50"
          style={{ background: "radial-gradient(circle, rgba(255,105,180,0.25) 0%, transparent 70%)" }}
        />

        {/* Close-Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 p-2 rounded-full bg-zinc-800/60 hover:bg-zinc-700/80 text-zinc-400 hover:text-white transition-all"
          aria-label="Schließen"
          data-testid="tipping-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className={`relative ${showCheckout ? "flex flex-col flex-1 min-h-0" : "p-7 sm:p-8"}`}>
          {showCheckout ? (
            <>
              {/* Checkout-Header (kompakt, nicht scrollend) */}
              <div className="text-center px-6 pt-6 pb-3 flex-shrink-0">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-full mb-2"
                     style={{ background: "rgba(255,105,180,0.15)", border: "1px solid rgba(255,105,180,0.4)" }}>
                  <Heart className="w-4 h-4" style={{ color: "#FF69B4" }} fill="#FF69B4" />
                </div>
                <h3 className="text-lg font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
                  Du bist die Beste 💜
                </h3>
                <p className="text-[11px] text-zinc-400 mt-0.5">
                  Sichere Zahlung über Ablefy · wähle einen Betrag, der sich gut anfühlt
                </p>
              </div>
              {/* Ablefy-Embed-Container — nimmt den restlichen Platz ein und scrollt selbst */}
              <div
                ref={wrapperRef}
                className="flex-1 min-h-0 overflow-y-auto bg-white mx-4 rounded-2xl"
                style={{ WebkitOverflowScrolling: "touch" }}
              >
                <div id="embedable-form-wrapper" style={{ minHeight: "500px" }} />
              </div>
              <p className="text-[10px] text-zinc-500 text-center py-2 flex-shrink-0">
                Powered by Ablefy · 100 % freiwillig · jederzeit abbrechbar
              </p>
            </>
          ) : (
            <>
              {/* Intro-Ansicht */}
              <div className="text-center mb-5">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-4 relative"
                     style={{ background: "rgba(255,105,180,0.15)", border: "1px solid rgba(255,105,180,0.4)" }}>
                  <Heart className="w-6 h-6" style={{ color: "#FF69B4" }} fill="#FF69B4" />
                  <Sparkles className="w-3 h-3 absolute -top-1 -right-1" style={{ color: "#C8FF4D" }} />
                </div>
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full mb-3"
                     style={{ borderColor: "rgba(255,105,180,0.3)", border: "1px solid rgba(255,105,180,0.3)", background: "rgba(255,105,180,0.05)" }}>
                  <span className="text-[10px] font-medium tracking-wider uppercase" style={{ color: "#FFB8DA" }}>
                    Ein kleiner Gedanke
                  </span>
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
                  Du bist Teil unserer Reise <span className="inline-block">💜</span>
                </h2>
              </div>

              <div className="space-y-3 text-sm text-zinc-300 leading-relaxed mb-6">
                <p>
                  Hi du Liebe! Schön, dass du <span className="text-white font-semibold">Social IQ</span> nutzt. ✨
                </p>
                <p>
                  Du hast deinen Zugang gekauft – das heißt, du zahlst <span className="text-cyan-300 font-semibold">keine laufenden Kosten</span> mehr.
                  Wir entwickeln die App aber jeden Tag mit ganz viel Liebe weiter, und dabei laufen Server-, KI- und Tool-Kosten für uns mit. 🌱
                </p>
                <p>
                  Wenn dir die App regelmäßig Wert gibt und du uns gerne ein bisschen unterstützen möchtest,
                  freuen wir uns von Herzen über ein <span className="font-semibold" style={{ color: "#FF69B4" }}>kleines Trinkgeld</span> –
                  ganz freiwillig, in einer Höhe, die sich für dich richtig anfühlt. ☕
                </p>
                <p className="text-xs text-zinc-500 italic">
                  Wir sehen jede einzelne Geste und sind dir dafür wirklich dankbar.
                </p>
              </div>

              <div className="space-y-2">
                <button
                  onClick={() => setShowCheckout(true)}
                  className="w-full py-3.5 px-5 rounded-2xl font-bold text-white transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg"
                  style={{
                    background: "linear-gradient(135deg, #FF69B4 0%, #FF1493 100%)",
                    boxShadow: "0 8px 24px rgba(255, 105, 180, 0.35)",
                  }}
                  data-testid="tipping-give-btn"
                >
                  <span className="inline-flex items-center justify-center gap-2">
                    <Heart className="w-4 h-4" fill="white" />
                    Ja, ich möchte unterstützen
                  </span>
                </button>
                {variant === "auto" && (
                  <button
                    onClick={onClose}
                    className="w-full py-2.5 px-5 rounded-2xl font-medium text-zinc-400 hover:text-zinc-200 transition-colors text-sm"
                    data-testid="tipping-later-btn"
                  >
                    Vielleicht später
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
